/**
 * Interview Digest Review — the wrap-up "review & selectively publish" screen
 * (88 · Task 2.3).
 *
 * At wrap-up the server returns a FACTUAL digest of what the founder said:
 *   • `proseDigest`     — their story told back to them (read-only, verbatim-safe).
 *   • `aboutDraft`      — a narrative "About" paragraph seeded into an editable field.
 *   • `publicSafeFields`— allowlisted, non-sensitive field proposals (tagline / etc.).
 *
 * The founder edits any field and independently toggles "publish to profile?" per
 * field. NOTHING is public by default. On confirm we publish ONLY the (toggled-on
 * ∩ allowlist) subset to the investor-visible `startup_profiles` row, then run the
 * caller's `onConfirmed` (the rescore path). Publish and rescore are DECOUPLED
 * (spec §1): declining to publish never skips the rescore, and publishing never
 * changes what scoring saw.
 *
 * PRESENTATION: a full-bleed panel styled to match `InterviewCompletion` (the
 * container renders it as an `absolute inset-0` overlay; the company profile
 * screen renders it as a full-screen overlay to re-open a stored digest). It owns
 * its own ScrollView + action row — never nest it inside another ScrollView.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, Pressable, ScrollView, TextInput } from 'react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { Check, Sparkles, Globe, Lock, ShieldCheck, RotateCcw } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { handleError, getUserMessage } from '@/lib/errors';
import { publishPublicSafeFields, PUBLIC_SAFE_KEYS } from '@/lib/interview/publish-profile';
import { supabase } from '@/lib/supabase/client';
import { loadMyStartupProfileRow } from '@/lib/startup/my-startup-profile';
import { mapStartupProfileRow } from '@/lib/supabase/profile-mappers';
import { useProfileStore } from '@/lib/state/profile-store';
import type { InterviewDigestResponse, DimensionCoverage } from '@/lib/interview/types';
import { CoverageByDimension } from './CoverageByDimension';
import {
  runDigestConfirm,
  type DigestFieldState,
} from './interview-digest-fields';

type Colors = ReturnType<typeof useThemeColors>;

/** Founder-friendly label + helper copy per allowlisted public-safe key. */
const FIELD_META: Record<string, { label: string; hint: string; multiline: boolean }> = {
  about: {
    label: 'About',
    hint: 'A short narrative investors see on your public profile.',
    multiline: true,
  },
  tagline: {
    label: 'Tagline',
    hint: 'Your one-liner — what you do, in a sentence.',
    multiline: false,
  },
  industry: {
    label: 'Industry',
    hint: 'The market you operate in.',
    multiline: false,
  },
  stage: {
    label: 'Stage',
    hint: 'Where your company is in its journey.',
    multiline: false,
  },
};

export interface InterviewDigestReviewProps {
  /** The completed session this digest belongs to (re-openable later). */
  sessionId: string;
  /** The founder's real startup id — the publish target. */
  startupId: string;
  /** The factual digest the server built (prose + About draft + public-safe proposals). */
  digest: Pick<InterviewDigestResponse, 'proseDigest' | 'aboutDraft' | 'publicSafeFields'>;
  /**
   * Per-dimension coverage (3-state markers) so the founder sees what's captured
   * and what's still to cover before finishing — the "structured per dimension"
   * view. Optional; omitted → the coverage section is not shown.
   */
  dimensionCoverage?: DimensionCoverage[];
  /**
   * Runs AFTER a clean publish (or a no-publish confirm). The interview
   * container wires this to complete + rescore; the profile re-open wires it to
   * simply close. Publish and this rescore path are decoupled.
   */
  onConfirmed: () => void;
  /**
   * "Skip publishing" — always available so the founder is never stuck. Defaults
   * to `onConfirmed` (in the wrap-up flow, skipping still runs the rescore).
   */
  onSkip?: () => void;
  /** Primary action label (defaults to the wrap-up copy). */
  confirmLabel?: string;
  /** Secondary action label. */
  skipLabel?: string;
}

/**
 * Seed the editable field rows from the digest. `about` prefers the dedicated
 * `aboutDraft` (the narrative headline), falling back to any `about` proposal;
 * the rest come straight from `publicSafeFields`. Every toggle starts OFF —
 * nothing is public until the founder explicitly approves it.
 */
function seedFieldStates(
  digest: InterviewDigestReviewProps['digest'],
): DigestFieldState[] {
  return PUBLIC_SAFE_KEYS.map((key) => ({
    key,
    value:
      key === 'about'
        ? digest.aboutDraft?.trim() || digest.publicSafeFields?.about?.trim() || ''
        : digest.publicSafeFields?.[key]?.trim() || '',
    publish: false,
  }));
}

export function InterviewDigestReview({
  startupId,
  digest,
  dimensionCoverage,
  onConfirmed,
  onSkip,
  confirmLabel = 'Publish & finish',
  skipLabel = 'Skip publishing',
}: InterviewDigestReviewProps) {
  const colors = useThemeColors();

  const [fields, setFields] = useState<DigestFieldState[]>(() => seedFieldStates(digest));
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const skip = onSkip ?? onConfirmed;

  const approvedCount = useMemo(
    () => fields.filter((f) => f.publish && f.value.trim().length > 0).length,
    [fields],
  );

  const setValue = useCallback((key: string, value: string) => {
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, value } : f)));
  }, []);

  const toggle = useCallback((key: string) => {
    Haptics.selectionAsync().catch(() => {});
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, publish: !f.publish } : f)));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (isPublishing) return;
    setIsPublishing(true);
    setPublishError(null);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
    try {
      const { error } = await runDigestConfirm({
        startupId,
        fieldStates: fields,
        publish: publishPublicSafeFields,
        onConfirmed,
      });
      if (error) {
        const { message } = handleError(error, { context: 'InterviewDigestReview.publish' });
        // Publish failed — keep the review open so the founder can retry or skip.
        // Rescore is untouched (runDigestConfirm did not fire onConfirmed).
        setPublishError(message);
      } else {
        // Publish succeeded — reload the STARTUP profile store (not the user auth
        // profile) so the scorecard picks up the newly-published about/tagline
        // WITHOUT a manual reload. The store's normal loader is guarded by
        // `!store.startupProfile`, so force a fresh read here (Adi 2026-07-22).
        try {
          const row = await loadMyStartupProfileRow(supabase);
          if (row) setStartupProfile(mapStartupProfileRow(row));
        } catch {
          // Best-effort: the About still lands on the next full profile load.
        }
      }
    } catch (err) {
      const { message } = handleError(err, { context: 'InterviewDigestReview.publish' });
      setPublishError(message);
    } finally {
      setIsPublishing(false);
    }
  }, [isPublishing, startupId, fields, onConfirmed, setStartupProfile]);

  const handleSkip = useCallback(() => {
    if (isPublishing) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    skip();
  }, [isPublishing, skip]);

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: isWeb ? 32 : 24,
          paddingBottom: 24,
        }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Eyebrow */}
        <Animated.View entering={FadeIn.duration(360)} className="items-center mb-5">
          <View
            className="flex-row items-center px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Sparkles size={13} color={colors.primary[600]} />
            <Text
              numberOfLines={1}
              className="text-[11px] font-bold ml-1.5"
              style={{ color: colors.primary[600], letterSpacing: 0.4 }}
            >
              YOUR STORY, IN YOUR WORDS
            </Text>
          </View>
        </Animated.View>

        <Text
          className="text-2xl font-bold text-center mb-2"
          style={{ color: colors.text.primary }}
        >
          Review what we captured
        </Text>
        <Text
          className="text-[15px] text-center leading-relaxed mb-6"
          style={{ color: colors.text.secondary, alignSelf: 'center', maxWidth: 420 }}
        >
          This is the factual recap from our chat. Nothing goes on your public profile
          unless you turn it on below.
        </Text>

        {/* Prose digest — read-only factual recap */}
        {!!digest.proseDigest?.trim() && (
          <Animated.View
            entering={FadeInDown.delay(80).duration(420)}
            className="rounded-2xl p-4 mb-6"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <View className="flex-row items-center mb-2" style={{ gap: 6 }}>
              <ShieldCheck size={15} color={colors.text.tertiary} />
              <Text
                numberOfLines={1}
                className="text-[11px] font-bold uppercase"
                style={{ color: colors.text.tertiary, letterSpacing: 0.5 }}
              >
                Factual recap
              </Text>
            </View>
            <Text className="text-[14px] leading-6" style={{ color: colors.text.secondary }}>
              {digest.proseDigest.trim()}
            </Text>
          </Animated.View>
        )}

        {/* Coverage by area — what's captured vs still to cover (per dimension) */}
        {!!dimensionCoverage?.length && (
          <Animated.View
            entering={FadeInDown.delay(120).duration(420)}
            className="rounded-2xl p-4 mb-6"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <CoverageByDimension dimensionCoverage={dimensionCoverage} />
          </Animated.View>
        )}

        {/* Section header for the publishable fields */}
        <View className="flex-row items-center mb-3" style={{ gap: 8 }}>
          <Globe size={16} color={colors.primary[600]} />
          <Text
            numberOfLines={1}
            className="text-base font-bold flex-1"
            style={{ color: colors.text.primary }}
          >
            Publish to your public profile
          </Text>
        </View>

        {/* Editable public-safe fields, each with an independent publish toggle */}
        {fields.map((field, index) => (
          <PublishableField
            key={`digest-field-${field.key}`}
            field={field}
            index={index}
            onChangeText={(v) => setValue(field.key, v)}
            onToggle={() => toggle(field.key)}
            colors={colors}
          />
        ))}

        {/* Publish error — recoverable, never a dead-end */}
        {publishError && (
          <Animated.View
            entering={FadeIn}
            className="rounded-2xl p-4 mt-2 mb-1"
            style={{ backgroundColor: colors.red.light }}
          >
            <Text className="text-sm mb-3" style={{ color: colors.error }}>
              {publishError}
            </Text>
            <Pressable
              onPress={handleConfirm}
              className="flex-row items-center self-start rounded-xl px-4 py-2"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <RotateCcw size={14} color={colors.text.inverse} />
              <Text
                className="text-sm font-semibold ml-2"
                style={{ color: colors.text.inverse }}
              >
                Try again
              </Text>
            </Pressable>
          </Animated.View>
        )}
      </ScrollView>

      {/* Action row — primary publish + always-available skip. Sits above the
          scroll so the founder never has to scroll to reach an action. */}
      <View
        className="px-5 pt-3 border-t"
        style={{
          backgroundColor: colors.surface.primary,
          borderColor: colors.border.light,
          paddingBottom: isWeb ? 16 : 10,
          gap: 10,
        }}
      >
        <View
          style={{
            height: 52,
            borderRadius: 14,
            overflow: 'hidden',
            backgroundColor: colors.primary[600],
            opacity: isPublishing ? 0.7 : 1,
          }}
        >
          <Pressable
            onPress={handleConfirm}
            disabled={isPublishing}
            accessibilityRole="button"
            accessibilityLabel={confirmLabel}
            className="flex-1 flex-row items-center justify-center"
            style={{ gap: 8 }}
          >
            <Check size={18} color={colors.text.inverse} strokeWidth={2.5} />
            <Text
              numberOfLines={1}
              className="text-[15px] font-bold"
              style={{ color: colors.text.inverse }}
            >
              {isPublishing
                ? 'Publishing…'
                : approvedCount > 0
                  ? `${confirmLabel} (${approvedCount})`
                  : confirmLabel}
            </Text>
          </Pressable>
        </View>

        <Pressable
          onPress={handleSkip}
          disabled={isPublishing}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={skipLabel}
          className="items-center justify-center py-2"
        >
          <Text
            numberOfLines={1}
            className="text-sm font-semibold"
            style={{ color: colors.text.tertiary }}
          >
            {skipLabel}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

// =============================================================================
// PublishableField — one editable value + a publish toggle (nothing on by default)
// =============================================================================

const PublishableField = React.memo(function PublishableField({
  field,
  index,
  onChangeText,
  onToggle,
  colors,
}: {
  field: DigestFieldState;
  index: number;
  onChangeText: (value: string) => void;
  onToggle: () => void;
  colors: Colors;
}) {
  const meta = FIELD_META[field.key] ?? { label: field.key, hint: '', multiline: false };
  const on = field.publish;

  return (
    <Animated.View
      entering={FadeInDown.delay(140 + index * 60).duration(400)}
      className="rounded-2xl p-4 mb-3"
      style={{
        backgroundColor: colors.surface.secondary,
        borderWidth: 1,
        borderColor: on ? colors.primary[300] : colors.border.light,
      }}
    >
      <View className="flex-row items-center justify-between mb-2" style={{ gap: 10 }}>
        <View className="flex-1">
          <Text
            numberOfLines={1}
            className="text-sm font-bold"
            style={{ color: colors.text.primary }}
          >
            {meta.label}
          </Text>
          {!!meta.hint && (
            <Text
              numberOfLines={2}
              className="text-[11px] mt-0.5"
              style={{ color: colors.text.tertiary }}
            >
              {meta.hint}
            </Text>
          )}
        </View>

        {/* Publish toggle — a labelled pill (keeps a text label per modal rules) */}
        <View
          style={{
            height: 32,
            borderRadius: 16,
            overflow: 'hidden',
            backgroundColor: on ? colors.primary[600] : colors.surface.tertiary,
          }}
        >
          <Pressable
            onPress={onToggle}
            accessibilityRole="switch"
            accessibilityState={{ checked: on }}
            accessibilityLabel={`Publish ${meta.label} to profile`}
            className="flex-1 flex-row items-center justify-center"
            style={{ paddingHorizontal: 12, gap: 5 }}
          >
            {on ? (
              <Check size={13} color={colors.text.inverse} strokeWidth={3} />
            ) : (
              <Lock size={12} color={colors.text.tertiary} strokeWidth={2.5} />
            )}
            <Text
              numberOfLines={1}
              className="text-[12px] font-bold"
              style={{ color: on ? colors.text.inverse : colors.text.tertiary }}
            >
              {on ? 'Publishing' : 'Private'}
            </Text>
          </Pressable>
        </View>
      </View>

      <TextInput
        value={field.value}
        onChangeText={onChangeText}
        placeholder={`Add your ${meta.label.toLowerCase()}…`}
        placeholderTextColor={colors.text.tertiary}
        multiline={meta.multiline}
        maxLength={meta.multiline ? 1200 : 160}
        className={`rounded-xl px-3 py-2.5 text-[14px] ${meta.multiline ? 'min-h-[88px]' : ''}`}
        style={{
          backgroundColor: colors.surface.primary,
          color: colors.text.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          textAlignVertical: meta.multiline ? 'top' : 'center',
        }}
      />
    </Animated.View>
  );
});
